import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'
import type {
  CatalogModule,
  CatalogModuleView,
  DeploymentType,
  Discipline,
  ModuleCategory,
  ModuleChangelogEntry,
  ModuleFeature,
  ModuleRequirement,
  PricingType,
  Priority,
  Project,
  ProjectColor,
  ProjectIconKey,
  ProjectFile,
  ProjectModule,
  ProjectStatus,
  Installation,
  HistoryEntry,
  Note,
  Release,
  ProjectSummary,
  ReleaseChannel,
  Sector,
  Task,
  TaskStatus,
  TaskType,
  TeamMember,
} from './types'
import {
  DEPLOYMENT_TYPES,
  DISCIPLINES,
  DOC_STATUSES,
  INSTALLATION_KINDS,
  INSTALLATION_STATUSES,
  MODULE_CATEGORIES,
  PRICING_TYPES,
  PRIORITIES,
  PROJECT_COLORS,
  PROJECT_ICONS,
  PROJECT_KINDS,
  PROJECT_STATUSES,
  RELEASE_CHANNELS,
  SEVERITIES,
  STAGE_STATUSES,
  TASK_STATUSES,
  TASK_TYPES,
} from './types'

type Row = Record<string, unknown>

export function rowsOf(data: unknown): Row[] {
  if (Array.isArray(data)) return data as Row[]
  if (data && typeof data === 'object') {
    const obj = data as Row
    for (const key of ['items', 'data', 'results', 'projects', 'modules', 'tasks', 'members', 'features', 'sectors', 'changelog']) {
      if (Array.isArray(obj[key])) return obj[key] as Row[]
    }
  }
  return asList(data as Row[] | { items?: Row[] })
}

function str(v: unknown, fallback = '') {
  return v == null ? fallback : String(v)
}
function num(v: unknown) {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}
export function money(v: unknown) {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toFixed(2)
  if (typeof v === 'string' && v.trim() && Number.isFinite(Number(v))) return Number(v).toFixed(2)
  return '0.00'
}
function oneOf<T extends string>(v: unknown, allowed: readonly T[], fallback: T): T {
  const s = String(v ?? '')
  return (allowed as readonly string[]).includes(s) ? (s as T) : fallback
}

const ZERO_PRICE = { one_time: '0.00', monthly: '0.00', yearly: '0.00' } as const

export function mapFeature(row: Row): ModuleFeature {
  return { id: str(row.id), group: str(row.group_name ?? row.group), name: str(row.title ?? row.name), description: str(row.description) }
}
export function mapChangelog(row: Row): ModuleChangelogEntry {
  return { id: str(row.id), version: str(row.version), date: str(row.release_date ?? row.date).slice(0, 10), notes: str(row.notes) }
}

export function mapCatalogModule(row: Row, extra?: { features?: ModuleFeature[]; changelog?: ModuleChangelogEntry[]; dependsOn?: string[]; usage?: CatalogModuleView['usage'] }): CatalogModuleView {
  const changelog = extra?.changelog ?? rowsOf(row.changelog).map(mapChangelog)
  const latest = [...changelog].sort((a, b) => b.date.localeCompare(a.date) || b.version.localeCompare(a.version))[0]
  return {
    id: str(row.id),
    code: str(row.code),
    name: str(row.name),
    description: str(row.description),
    category: oneOf(row.category, MODULE_CATEGORIES, 'core'),
    pricing: oneOf(row.pricing_type ?? row.pricing, PRICING_TYPES, 'one_time'),
    price: money(row.list_price ?? row.price),
    dependsOn: extra?.dependsOn ?? (Array.isArray(row.depends_on) ? (row.depends_on as unknown[]).map(String) : []),
    active: row.is_active !== false && row.active !== false,
    version: latest?.version ?? str(row.version),
    setupHours: num(row.est_setup_hours ?? row.setup_hours ?? row.setupHours),
    trialDays: num(row.trial_days ?? row.trialDays),
    requirements: Array.isArray(row.technical_requirements) ? (row.technical_requirements as unknown[]).map(String) : Array.isArray(row.requirements) ? (row.requirements as unknown[]).map(String) : [],
    features: extra?.features ?? rowsOf(row.features).map(mapFeature),
    changelog,
    updatedAt: str(row.updated_at ?? row.updatedAt),
    usage: extra?.usage ?? [],
  }
}

export function mapSector(row: Row): Sector {
  const ids = row.project_ids ?? row.projectIds
  return {
    id: str(row.id),
    name: str(row.name),
    description: str(row.description),
    projectIds: Array.isArray(ids) ? ids.map(String) : [],
  }
}

export function priorityOf(v: unknown): Priority {
  const s = String(v ?? '')
  if (s === 'normal') return 'medium'
  return oneOf(v, PRIORITIES, 'medium')
}
export function priorityBody(p: Priority) {
  return p === 'medium' ? 'normal' : p
}
/** Pano sütunu → görev durumu (teknik pano: backlog/ready → todo, waiting → in_progress). */
export function taskStatusOf(v: unknown): TaskStatus {
  const s = String(v ?? '').replace('-', '_')
  if (s === 'backlog' || s === 'ready') return 'todo'
  if (s === 'waiting') return 'in_progress'
  return oneOf(s, TASK_STATUSES, 'todo')
}

/** Görev durumu → teknik pano sütunu. */
export function columnOf(status: TaskStatus): string {
  return status === 'todo' ? 'backlog' : status
}

const TYPE_TO_API: Record<TaskType, string> = { development: 'gelistirme', installation: 'kurulum', demo: 'demo' }
export const taskTypeBody = (t: TaskType) => TYPE_TO_API[t]
function taskTypeOf(v: unknown): TaskType {
  const s = String(v ?? '')
  if (s === 'kurulum') return 'installation'
  if (s === 'demo') return 'demo'
  if (s === 'gelistirme') return 'development'
  return oneOf(s, TASK_TYPES, 'development')
}

const PERSON_KINDS = new Set(['assigned', 'reviewer', 'item_assigned', 'assignment_requested', 'assignment_approved', 'assignment_rejected', 'assignment_cancelled'])

function mapHistory(row: Row): HistoryEntry {
  let kind = str(row.kind)
  const to = str(row.to)
  if (kind === 'assigned' && !to) kind = 'unassigned'
  if (kind === 'checklist' && str(row.note) === 'deleted') kind = 'checklist_deleted'
  return {
    id: str(row.id),
    at: str(row.created_at),
    by: str(row.user_id),
    kind,
    status: kind === 'status' || kind === 'created' ? taskStatusOf(to) : undefined,
    detail: kind === 'priority' ? priorityOf(to) : to,
    userId: PERSON_KINDS.has(kind) ? to : undefined,
    note: kind === 'checklist' ? to : str(row.note),
  }
}

export function mapTask(row: Row): Task {
  return {
    id: str(row.id),
    key: str(row.task_key ?? row.key),
    title: str(row.title),
    description: str(row.description),
    type: taskTypeOf(row.task_type ?? row.type),
    status: taskStatusOf(row.column_key ?? row.status),
    priority: priorityOf(row.priority),
    assigneeId: row.assignee_user_id == null && row.assignee_id == null && row.assigneeId == null ? null : str(row.assignee_user_id ?? row.assignee_id ?? row.assigneeId) || null,
    reviewerId: str(row.reviewer_user_id) || null,
    watcherIds: [],
    installationId: row.installation_id == null ? null : str(row.installation_id) || null,
    parentId: row.parent_id == null ? null : str(row.parent_id) || null,
    dependsOn: Array.isArray(row.depends_on) ? row.depends_on.map(String) : [],
    sprint: str(row.sprint),
    sprintId: str(row.sprint_id) || null,
    startDate: str(row.start_date ?? row.startDate).slice(0, 10),
    dueDate: str(row.due_date ?? row.dueDate).slice(0, 10),
    estimateHours: num(row.estimated_hours ?? row.estimate_hours ?? row.estimateHours),
    spentHours: num(row.spent_hours ?? row.spentHours),
    checklist: rowsOf(row.checklist).map((c) => ({
      id: str(c.id),
      text: str(c.title),
      done: c.is_done === true,
      assigneeId: str(c.assignee_user_id) || null,
      dueDate: str(c.due_date).slice(0, 10),
      doneBy: str(c.done_by) || null,
    })),
    comments: rowsOf(row.comments).map((c) => ({
      id: str(c.id),
      author: str(c.author_user_id),
      body: str(c.body),
      at: str(c.created_at),
      visibility: c.visibility === 'shared' ? 'shared' : 'internal',
    })),
    history: rowsOf(row.history).map(mapHistory),
    createdAt: str(row.created_at ?? row.createdAt),
  }
}

export function mapMember(row: Row): TeamMember {
  return {
    id: str(row.id),
    userId: str(row.user_id ?? row.userId),
    discipline: oneOf(row.area ?? row.discipline, DISCIPLINES, 'management'),
    title: str(row.title),
    joinedAt: str(row.created_at ?? row.joined_at ?? row.joinedAt).slice(0, 10),
  }
}

function taskStats(tasks: Task[]) {
  const done = tasks.filter((t) => t.status === 'done').length
  return {
    activeTasks: tasks.filter((t) => t.status !== 'done').length,
    pendingApproval: tasks.filter((t) => t.status === 'approval').length,
    progress: tasks.length ? Math.round((done / tasks.length) * 100) : 0,
  }
}

export function mapSummary(row: Row, tasks: Task[] = []): ProjectSummary {
  const stats = taskStats(tasks)
  const icon = oneOf(row.icon, PROJECT_ICONS, 'package')
  const color = oneOf(row.color ?? row.color_code, PROJECT_COLORS, 'blue')
  return {
    id: str(row.id),
    key: str(row.code ?? row.key),
    name: str(row.name),
    description: str(row.description),
    icon,
    color,
    status: oneOf(row.status, PROJECT_STATUSES, 'active'),
    kind: oneOf(row.kind, PROJECT_KINDS, 'internal'),
    sectorId: str(row.sector_id),
    deploymentType: row.deployment_type == null && row.deploymentType == null ? null : oneOf(row.deployment_type ?? row.deploymentType, DEPLOYMENT_TYPES, 'customer_server'),
    startDate: str(row.start_date ?? row.startDate).slice(0, 10),
    endDate: str(row.end_date ?? row.endDate).slice(0, 10),
    version: row.version == null ? null : str(row.version) || null,
    basePrice: { ...ZERO_PRICE, one_time: money(row.list_price ?? row.base_price) },
    moduleCount: num(row.module_count ?? row.moduleCount),
    activeTasks: num(row.active_tasks) || stats.activeTasks,
    overdueTasks: num(row.overdue_tasks),
    pendingApproval: num(row.pending_approval) || stats.pendingApproval,
    activeInstallations: num(row.active_installations),
    progress: row.progress == null ? stats.progress : num(row.progress),
    memberIds: Array.isArray(row.member_ids) ? (row.member_ids as unknown[]).map(String) : [],
  }
}

export function mapProjectModule(row: Row, catalog?: CatalogModule): ProjectModule {
  const requirement: ModuleRequirement = row.is_mandatory === true || row.requirement === 'required' ? 'required' : 'optional'
  return {
    id: str(row.module_id ?? row.id),
    code: str(row.code ?? catalog?.code),
    name: str(row.name ?? catalog?.name),
    description: str(row.description ?? catalog?.description),
    category: oneOf(row.category ?? catalog?.category, MODULE_CATEGORIES, 'core'),
    requirement,
    pricing: oneOf(row.pricing_type ?? catalog?.pricing, PRICING_TYPES, 'one_time'),
    price: money(row.project_price ?? row.price ?? catalog?.price),
    dependsOn: catalog?.dependsOn ?? [],
  }
}

export async function loadTasks(projectId?: string) {
  return rowsOf(await api.get<unknown>('/api/v1/tasks', projectId ? { project_id: projectId, include: 'details' } : undefined)).map(mapTask)
}

function dayOf(v: unknown) {
  return str(v).slice(0, 10)
}

export function mapInstallation(row: Row): Installation {
  const stages = rowsOf(row.stages).map((s) => ({
    id: str(s.id),
    name: str(s.title),
    description: str(s.description),
    ownerId: str(s.assignee_user_id) || null,
    plannedDate: dayOf(s.planned_date),
    completion: num(s.completion),
    status: oneOf(s.status, STAGE_STATUSES, 'pending'),
    taskIds: Array.isArray(s.task_ids) ? s.task_ids.map(String) : [],
  }))
  return {
    id: str(row.id),
    accountId: str(row.account_id),
    customer: str(row.account_title),
    city: str(row.city),
    kind: oneOf(row.run_type, INSTALLATION_KINDS, 'installation'),
    ownerId: str(row.assignee_user_id) || null,
    startDate: dayOf(row.scheduled_on),
    targetDate: dayOf(row.target_date),
    status: oneOf(row.status, INSTALLATION_STATUSES, 'planned'),
    progress: stages.length ? Math.round(stages.reduce((sum, s) => sum + (s.status === 'done' ? 100 : s.completion), 0) / stages.length) : 0,
    stages,
    blockers: rowsOf(row.blockers).map((b) => ({
      id: str(b.id),
      text: str(b.title),
      severity: oneOf(b.severity, SEVERITIES, 'medium'),
      ownerId: str(b.owner_user_id) || null,
      date: dayOf(b.date),
      resolved: b.is_resolved === true,
    })),
    documents: rowsOf(row.documents).map((d) => ({ id: str(d.id), name: str(d.name), date: dayOf(d.date), status: oneOf(d.status, DOC_STATUSES, 'waiting') })),
  }
}

export function mapNote(row: Row): Note {
  return { id: str(row.id), title: str(row.title), body: str(row.body), author: str(row.author_user_id), pinned: row.pinned === true, updatedAt: str(row.updated_at) }
}

export function mapProjectFile(row: Row): ProjectFile {
  return {
    id: str(row.id),
    name: str(row.file_name),
    size: num(row.size_bytes),
    mime: str(row.content_type),
    uploadedAt: str(row.created_at),
    uploadedBy: str(row.uploaded_by),
    taskId: str(row.task_id) || null,
  }
}

export function mapRelease(row: Row): Release {
  return { id: str(row.id), version: str(row.version), channel: oneOf(row.channel, RELEASE_CHANNELS, 'stable'), releasedAt: dayOf(row.released_on), notes: str(row.notes) }
}

/** Proje detayı paralel istekler: özet, ekip, modüller, görevler (ayrıntılı), çalışma alanı, kurulumlar. */
export async function loadProject(id: string): Promise<Project> {
  const [hub, memberRows, moduleRows, tasks, workspace, installations] = await Promise.all([
    api.get<Row>(`/api/v1/projects/${id}`),
    api.get<unknown>(`/api/v1/projects/${id}/members`).then(rowsOf),
    api.get<unknown>(`/api/v1/projects/${id}/modules`).then(rowsOf),
    loadTasks(id),
    api.get<Row>(`/api/v1/projects/${id}/workspace`).catch(() => ({}) as Row),
    // Kurulumlar CRM hesap yetkisi ister; yetki yoksa sekme boş kalır.
    api
      .get<unknown>('/api/v1/crm/deployments', { project_id: id, include: 'details' })
      .then(rowsOf)
      .catch(() => [] as Row[]),
  ])
  const projectRow = (hub.project && typeof hub.project === 'object' ? hub.project : hub) as Row
  const summary = mapSummary(projectRow, tasks)
  const team = memberRows.map(mapMember)
  return {
    ...summary,
    moduleCount: moduleRows.length || summary.moduleCount,
    memberIds: team.map((m) => m.userId),
    createdAt: str(projectRow.created_at ?? projectRow.createdAt),
    updatedAt: str(projectRow.updated_at ?? projectRow.updatedAt),
    team,
    tasks,
    installations: installations.map(mapInstallation),
    notes: rowsOf(workspace.notes).map(mapNote),
    files: rowsOf(workspace.files).map(mapProjectFile),
    modules: moduleRows.map((row) => mapProjectModule(row)),
    releases: rowsOf(workspace.releases).map(mapRelease),
  }
}

export function moduleBody(input: Pick<CatalogModule, 'name' | 'category' | 'pricing' | 'price' | 'description' | 'active' | 'setupHours' | 'trialDays' | 'requirements'> & { code?: string }) {
  return {
    code: input.code,
    name: input.name,
    category: input.category,
    pricing_type: input.pricing,
    list_price: input.price,
    description: input.description,
    is_active: input.active,
    est_setup_hours: input.setupHours,
    trial_days: input.trialDays,
    technical_requirements: input.requirements,
  }
}

export type { ModuleCategory, PricingType, ProjectColor, ProjectIconKey, ProjectStatus, ReleaseChannel, TaskType, DeploymentType, Discipline }
export { ZERO_PRICE }
